import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardSection, Button } from '@trafiflow/shared-ui'
import { agentplatService } from '../../../services/agentplat'
import type { Agent } from '../../../services/agentplat/types'
import { Search, Gift, User, Sparkles, Loader2, X, CheckCircle2, Filter } from 'lucide-react'
import { AgentContractModal } from '../components'
import apiClient from '../../../shared/lib/api'
import { ROUTES } from '../../../shared/config/routes'

export default function AgentMarket() {
    const navigate = useNavigate()
    const [agents, setAgents] = useState<Agent[]>([])
    const [contractedAgents, setContractedAgents] = useState<Agent[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [roleFilter, setRoleFilter] = useState<string>('all')
    const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [showContractModal, setShowContractModal] = useState(false)
    const [contractAgent, setContractAgent] = useState<Agent | null>(null)
    const [isCreatingContract, setIsCreatingContract] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [successAgentName, setSuccessAgentName] = useState<string>('')

    const fetchAgents = async (showLoading = true) => {
        try {
            if (showLoading) {
                setLoading(true)
            }
            setError(null)
            // Fetch market agents and contracted agents in parallel
            const [marketAgents, userAgents] = await Promise.all([
                agentplatService.listMarketAgents(),
                agentplatService.listAgents().catch(() => []) // If it fails, just use empty array
            ])
            setAgents(marketAgents)
            setContractedAgents(userAgents)
        } catch (error: unknown) {
            const err = error as { response?: { status?: number; data?: { message?: string; error?: string } }; message?: string }
            console.error('Error fetching market agents:', err)
            setError(err.message || 'Failed to load agents. Please try again later.')
        } finally {
            if (showLoading) {
                setLoading(false)
            }
        }
    }

    useEffect(() => {
        fetchAgents()
    }, [])

    // Extract unique roles from agents
    const availableRoles = useMemo(() => {
        const roleSet = new Set<string>()
        agents.forEach(agent => {
            if (agent.role) {
                const roleName = typeof agent.role === 'string' ? agent.role : agent.role.name || agent.role.id
                if (roleName) {
                    roleSet.add(roleName)
                }
            }
        })
        return Array.from(roleSet).sort()
    }, [agents])

    // Get role name from agent
    const getRoleName = (agent: Agent): string => {
        if (!agent.role) return ''
        return typeof agent.role === 'string' ? agent.role : agent.role.name || agent.role.id || ''
    }

    // Filter agents based on search query, role filter, and exclude contracted agents
    const filteredAgents = useMemo(() => {
        // Create a set of contracted agent IDs for fast lookup
        const contractedAgentIds = new Set(contractedAgents.map(agent => agent.id))
        
        return agents.filter(agent => {
            // Exclude agents that are already contracted
            if (contractedAgentIds.has(agent.id)) {
                return false
            }
            
            // Filter by role
            if (roleFilter !== 'all') {
                const agentRoleName = getRoleName(agent)
                if (agentRoleName !== roleFilter) {
                    return false
                }
            }
            
            // Filter by search query
            const query = searchQuery.toLowerCase()
            if (!query) {
                return true // Show all agents if no search query
            }
            
            return (
                agent.name.toLowerCase().includes(query) ||
                agent.description?.toLowerCase().includes(query) ||
                getRoleName(agent).toLowerCase().includes(query)
            )
        })
    }, [agents, contractedAgents, searchQuery, roleFilter])

    const handleCardClick = (agent: Agent) => {
        console.log('Card clicked:', agent.name)
        setSelectedAgent(agent)
        setShowDetailModal(true)
    }

    const handleHireAgent = (agent: Agent) => {
        setContractAgent(agent)
        setShowContractModal(true)
    }

    const handleAcceptContract = async () => {
        if (!contractAgent) return

        try {
            setIsCreatingContract(true)
            console.log('Accepting contract for agent:', contractAgent.name)

            // Create contract via API
            const response = await apiClient.post('/billing/contracts', {
                agent_id: contractAgent.id,
                agent_name: contractAgent.name,
                price: contractAgent.price || 0,
                trial_period_days: contractAgent.trialPeriodDays,
            })

            console.log('Contract created successfully:', response.data)
            
            // Check cloning status from response
            const cloningStatus = response.data?.cloning?.status
            const cloningError = response.data?.cloning?.error
            const clonedAgentId = response.data?.cloning?.cloned_agent_id
            
            // Reload agents to update the list (this will filter out the newly contracted agent)
            // Don't show loading indicator since success modal will be shown
            await fetchAgents(false)
            
            // Close modal and reset state
            setShowContractModal(false)
            setSuccessAgentName(contractAgent.name)
            setContractAgent(null)
            
            // Show warning if cloning failed
            if (cloningStatus === 'failed') {
                console.warn('Agent cloning failed:', cloningError)
                alert(
                    `⚠️ Contract created successfully, but agent cloning failed.\n\n` +
                    `Error: ${cloningError || 'Unknown error'}\n\n` +
                    `The agent may not appear in "My Agents" immediately. ` +
                    `Please contact support if the problem persists.`
                )
            } else if (cloningStatus === 'success') {
                console.log('Agent cloned successfully. Cloned agent ID:', clonedAgentId)
            }
            
            // Show success modal
            setShowSuccessModal(true)
        } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string; error?: string } }; message?: string }
            console.error('Error creating contract:', err)
            const errorMessage = err.response?.data?.error || err.message || 'Failed to create contract'
            alert(`Error creating contract: ${errorMessage}`)
        } finally {
            setIsCreatingContract(false)
        }
    }

    const handleCloseContractModal = () => {
        setShowContractModal(false)
        setContractAgent(null)
    }

    const handleCloseModal = () => {
        setShowDetailModal(false)
        setSelectedAgent(null)
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    <p className="text-tf-text-muted">Loading agents from marketplace...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="p-8 text-center bg-tf-feedback-error-bg rounded-lg border border-tf-feedback-error-border">
                <p className="text-tf-feedback-error-text font-medium">{error}</p>
                <Button
                    onClick={() => window.location.reload()}
                    variant="primary"
                    className="mt-4"
                >
                    Retry
                </Button>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
                <div>
                    <h1 className="text-2xl font-bold text-tf-text">Agent Market</h1>
                    <p className="text-tf-text-muted mt-1">
                        Explore and hire pre-configured AI agents from experts to optimize your sales, customer support, and marketing.
                    </p>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col gap-4 md:flex-row">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-tf-text-muted" />
                    <input
                        type="text"
                        placeholder="Search agents by name or description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                </div>
                {availableRoles.length > 0 && (
                    <div className="relative">
                        <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-tf-text-muted" />
                        <select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            className="w-full md:w-64 pl-10 pr-4 py-2.5 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-tf-bg-card"
                        >
                            <option value="all">All roles</option>
                            {availableRoles.map((role) => (
                                <option key={role} value={role}>
                                    {role}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between">
                <p className="text-sm text-tf-text-muted">
                    {filteredAgents.length} agente{filteredAgents.length !== 1 ? 's' : ''} disponible{filteredAgents.length !== 1 ? 's' : ''} para contratar
                    {searchQuery || roleFilter !== 'all' ? (
                        <span className="text-tf-text-muted">
                            {' '}(de {agents.length} total{agents.length !== 1 ? 'es' : ''})
                        </span>
                    ) : null}
                </p>
                {(searchQuery || roleFilter !== 'all') && (
                    <button
                        onClick={() => {
                            setSearchQuery('')
                            setRoleFilter('all')
                        }}
                        className="text-sm text-primary hover:text-primary/80 font-medium"
                    >
                        Limpiar filtros
                    </button>
                )}
            </div>

            {/* Agents Grid */}
            {filteredAgents.length === 0 ? (
                <Card className="p-12 text-center">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-tf-bg-hover rounded-full flex items-center justify-center">
                            <Sparkles className="w-8 h-8 text-tf-text-muted" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-tf-text">
                                {searchQuery ? 'No agents found' : 'No agents available to purchase'}
                            </h3>
                            <p className="text-tf-text-muted max-w-sm mx-auto mt-2">
                                {searchQuery
                                    ? 'Try different search terms.'
                                    : 'No agents are available in the marketplace at this time. Check back later for new options.'}
                            </p>
                        </div>
                        {(searchQuery || roleFilter !== 'all') && (
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setSearchQuery('')
                                    setRoleFilter('all')
                                }}
                            >
                                Limpiar filtros
                            </Button>
                        )}
                    </div>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredAgents.map((agent) => (
                        <Card 
                            key={agent.id} 
                            className="hover:shadow-lg transition-all duration-200 flex flex-col cursor-pointer"
                            onClick={() => handleCardClick(agent)}
                        >
                            <CardSection className="flex-1">
                                <div className="flex items-start gap-4 mb-4">
                                    {agent.avatar ? (
                                        <img
                                            src={agent.avatar}
                                            alt={agent.name}
                                            className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border border-tf-border"
                                        />
                                    ) : (
                                        <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-tf-border">
                                            <User className="w-8 h-8 text-primary" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-bold text-tf-text truncate">{agent.name}</h3>
                                        {agent.role && (
                                            <div className="mt-1">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-tf-feedback-info-bg text-tf-feedback-info-text">
                                                    {getRoleName(agent)}
                                                </span>
                                            </div>
                                        )}
                                        {agent.status && (
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`w-2 h-2 rounded-full ${agent.status === 'online' || agent.status === 'active' ? 'bg-tf-success' : 'bg-tf-text-muted'}`}></span>
                                                <span className="text-xs font-medium text-tf-text-muted capitalize">
                                                    {agent.status}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <p className="text-sm text-tf-text-muted line-clamp-3 mb-4">
                                    {agent.description || 'No description available.'}
                                </p>
                            </CardSection>
                            <CardSection className="bg-tf-bg-muted/50 pt-4 pb-4">
                                {agent.price !== undefined && agent.price !== null && agent.price > 0 ? (
                                    <Button
                                        variant="primary"
                                        fullWidth
                                        icon={<Gift className="w-4 h-4" />}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleHireAgent(agent)
                                        }}
                                    >
                                        Try for Free
                                    </Button>
                                ) : (
                                    <div className="w-full px-4 py-2.5 text-center text-sm font-medium text-tf-text-muted bg-tf-bg-hover rounded-lg border border-tf-border">
                                        Available Soon
                                    </div>
                                )}
                            </CardSection>
                        </Card>
                    ))}
                </div>
            )}

            {/* Agent Detail Modal */}
            {showDetailModal && selectedAgent && (
                <>
                    {/* Overlay */}
                    <div 
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={handleCloseModal}
                    />
                    {/* Modal */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <CardSection>
                                <div className="flex items-start justify-between mb-6">
                                    <div className="flex items-start gap-4 flex-1">
                                        {selectedAgent.avatar ? (
                                            <img
                                                src={selectedAgent.avatar}
                                                alt={selectedAgent.name}
                                                className="w-20 h-20 rounded-lg object-cover flex-shrink-0 border border-tf-border"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-tf-border">
                                                <User className="w-10 h-10 text-primary" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <h2 className="text-2xl font-bold text-tf-text mb-2">
                                                {selectedAgent.name}
                                            </h2>
                                            {selectedAgent.role && (
                                                <div className="mb-2">
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-tf-feedback-info-bg text-tf-feedback-info-text">
                                                        {getRoleName(selectedAgent)}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedAgent.status && (
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className={`w-2 h-2 rounded-full ${selectedAgent.status === 'online' || selectedAgent.status === 'active' ? 'bg-tf-success' : 'bg-tf-text-muted'}`}></span>
                                                    <span className="text-sm font-medium text-tf-text-muted capitalize">
                                                        {selectedAgent.status}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCloseModal}
                                        className="text-tf-text-muted hover:text-tf-text-muted transition-colors"
                                        aria-label="Close"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Agent ID - Discreto */}
                                <div className="mb-4 pb-4 border-b border-tf-border-sub">
                                    <p className="text-xs text-tf-text-muted font-mono">
                                        ID: {selectedAgent.id}
                                    </p>
                                </div>

                                <div className="space-y-4 mb-6">
                                    <div>
                                        <h3 className="text-sm font-semibold text-tf-text-sub mb-2">
                                            Description
                                        </h3>
                                        <p className="text-sm text-tf-text-muted leading-relaxed">
                                            {selectedAgent.description || 'No description available.'}
                                        </p>
                                    </div>

                                    {selectedAgent.lastActivity && (
                                        <div>
                                            <h3 className="text-sm font-semibold text-tf-text-sub mb-2">
                                                Last Activity
                                            </h3>
                                            <p className="text-sm text-tf-text-muted">
                                                {selectedAgent.lastActivity}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-3 pt-4 border-t border-tf-border">
                                    <Button
                                        variant="secondary"
                                        onClick={handleCloseModal}
                                        fullWidth
                                    >
                                        Close
                                    </Button>
                                    {selectedAgent.price !== undefined && selectedAgent.price !== null && selectedAgent.price > 0 ? (
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                handleHireAgent(selectedAgent)
                                                handleCloseModal()
                                            }}
                                            fullWidth
                                            icon={<Gift className="w-4 h-4" />}
                                        >
                                            Try for Free
                                        </Button>
                                    ) : (
                                        <div className="flex-1 px-4 py-2.5 text-center text-sm font-medium text-tf-text-muted bg-tf-bg-hover rounded-lg border border-tf-border flex items-center justify-center">
                                            Available Soon
                                        </div>
                                    )}
                                </div>
                            </CardSection>
                        </Card>
                    </div>
                </>
            )}

            {/* Agent Contract Modal */}
            {contractAgent && (
                <AgentContractModal
                    agent={contractAgent}
                    isOpen={showContractModal}
                    onClose={handleCloseContractModal}
                    onAccept={handleAcceptContract}
                    isLoading={isCreatingContract}
                />
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <>
                    {/* Overlay */}
                    <div
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={() => setShowSuccessModal(false)}
                    />
                    {/* Modal */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <Card className="w-full max-w-md">
                            <CardSection className="text-center">
                                <div className="flex flex-col items-center gap-4 mb-6">
                                    <div className="w-16 h-16 bg-tf-feedback-success-bg rounded-full flex items-center justify-center">
                                        <CheckCircle2 className="w-10 h-10 text-tf-feedback-success-text" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-tf-text mb-2">
                                            Contract Created!
                                        </h2>
                                        <p className="text-tf-text-muted">
                                            The agent <span className="font-semibold">{successAgentName}</span> has been successfully contracted.
                                        </p>
                                        <p className="text-sm text-tf-text-muted mt-2">
                                            The agent should appear in "My Agents" in a few moments.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setShowSuccessModal(false)
                                            // The list is already reloaded, so the contracted agent won't appear
                                        }}
                                        fullWidth
                                    >
                                        Stay Here
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            setShowSuccessModal(false)
                                            navigate(ROUTES.AGENTS.MY_AGENTS)
                                        }}
                                        fullWidth
                                    >
                                        Go to My Agents
                                    </Button>
                                </div>
                            </CardSection>
                        </Card>
                    </div>
                </>
            )}
        </div>
    )
}
